import { FC } from 'react';
import { Button, Card, InputNumber, Select, Space, Spin, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import './ApiCard.scss';
import { HttpMethod, Exception, Endpoint } from 'types/SiteAdministration';
import { METHOD_STYLES, UNIT_OPTIONS } from 'shared/constants';

interface UserOption {
  label: string;
  value: string;
}

/* ----------------------------------------------------------------------------
 * Method Badge
 * ------------------------------------------------------------------------- */

const MethodBadge: FC<{ method: HttpMethod }> = ({ method }) => (
  <span className={`px-3 py-1 rounded-md text-sm font-bold ${METHOD_STYLES[method]}`}>{method}</span>
);

/* ----------------------------------------------------------------------------
 * Exception Section
 * ------------------------------------------------------------------------- */

const ExceptionSection: FC<any> = ({ exceptions, onAddRow, onClearAll, onUpdate, onRemove, userOptions }) => {
  const normalizedUserOptions: UserOption[] = (userOptions || []).map((opt: any) => ({
    label: String(opt?.label ?? ''),
    value: String(opt?.value ?? '')
  }));

  const getFilteredOptionsForRow = (rowIndex: number): UserOption[] => {
    const selectedInOtherRows = new Set(
      (exceptions || [])
        .filter((_: Exception, idx: number) => idx !== rowIndex)
        .flatMap((exception: Exception) => exception.users || [])
    );
    const selectedInCurrentRow = new Set((exceptions?.[rowIndex]?.users || []).map(String));

    return normalizedUserOptions.filter(
      (option) => !selectedInOtherRows.has(option.value) || selectedInCurrentRow.has(option.value)
    );
  };

  return (
    <div className="bg-white border rounded-lg p-4 mb-4">
      <div className="flex justify-between mb-3">
        <span className="font-semibold">Add Exceptions</span>
        <Space>
          <Button className="font-semibold" type="link" danger onClick={onClearAll}>
            Remove All
          </Button>
          <Button className="font-semibold" type="link" icon={<PlusOutlined />} onClick={onAddRow}>
            Add Row
          </Button>
        </Space>
      </div>

      {exceptions.map((ex: Exception, idx: number) => (
        <div key={idx} className="flex gap-2 mb-2 items-start">
          <Select
            mode="multiple"
            className="flex-1"
            value={ex.users}
            options={getFilteredOptionsForRow(idx)}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            onChange={(users) => {
              const selectedInOtherRows = new Set(
                (exceptions || [])
                  .filter((_: Exception, rowIdx: number) => rowIdx !== idx)
                  .flatMap((exception: Exception) => exception.users || [])
              );
              const distinctUsers = (users || []).filter((userId: string) => !selectedInOtherRows.has(userId));
              onUpdate(idx, { users: distinctUsers });
            }}
          />
          <InputNumber
            min={0}
            value={ex.limit ?? undefined}
            onChange={(limit) => onUpdate(idx, { limit })}
            className="w-24"
          />
          <Select
            value={ex.unit}
            options={UNIT_OPTIONS}
            onChange={(unit) => onUpdate(idx, { unit })}
            className="w-24"
          />
          <Button icon={<DeleteOutlined />} onClick={() => onRemove(idx)} />
        </div>
      ))}
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * Endpoint Row
 * ------------------------------------------------------------------------- */

const EndpointRow: FC<any> = ({
  endpoint,
  index,
  isEditing,
  showExceptions,
  userOptions,
  onUpdateEndpoint,
  onAddException,
  onClearExceptions,
  onUpdateException,
  onRemoveException
}) => {
  const hasExceptions = showExceptions && endpoint.exceptions?.length;

  return (
    <div className="mb-2">
      {/* ✅ SINGLE SHARED CONTAINER */}
      <div className="bg-gray-100 rounded-lg px-4 py-3 flex flex-col">
        {/* ───────────── Endpoint Row ───────────── */}
        <div className="flex items-center gap-3">
          <MethodBadge method={endpoint.method} />

          {!isEditing ? (
            <div className="flex items-center flex-1 min-w-0">
              <span className="text-gray-800 font-medium text-lg whitespace-nowrap">{endpoint.path}</span>

              <span className="flex-1 mx-[10px] h-[1px] min-w-[10px] bg-gray-300" />

              <span className="text-gray-700 font-semibold text-lg whitespace-nowrap">
                {endpoint.limit} Requests/{endpoint.unit.slice(0, 3).toLowerCase()}
              </span>
            </div>
          ) : (
            <>
              <div className="flex-1">
                <div>{endpoint.path}</div>

                {showExceptions && !endpoint.exceptions?.length && (
                  <Button type="link" onClick={() => onAddException?.(index)}>
                    Add Exceptions
                  </Button>
                )}
              </div>

              <InputNumber min={0} value={endpoint.limit} onChange={(limit) => onUpdateEndpoint(index, { limit })} />

              <Select
                value={endpoint.unit}
                options={UNIT_OPTIONS}
                onChange={(unit) => onUpdateEndpoint(index, { unit })}
              />
            </>
          )}
        </div>

        {/* ───────────── Exceptions (VIEW MODE) ───────────── */}
        {showExceptions && endpoint.exceptions?.length > 0 && !isEditing && (
          <div className="mt-3">
            {/* HEADER */}
            <div className="text-xs font-medium text-gray-500 tracking-wider pl-16">ADDED EXCEPTIONS</div>

            {endpoint.exceptions.map((ex: Exception, i: number) => (
              <div key={i} className="flex items-start gap-3 pl-16 mt-2">
                <div className="flex flex-wrap gap-2">
                  {ex.users.map((u) => {
                    const label = userOptions.find((opt: any) => opt.value === u)?.label || u;
                    return (
                      <Tag key={u} className="bg-white border-0 rounded-md text-black px-2 py-[2px]">
                        {label}
                      </Tag>
                    );
                  })}
                </div>

                <span className="flex-1 mx-[4px] h-[1px] min-w-[25px] bg-gray-300 mt-[12px]" />

                <span className="text-gray-700 font-semibold text-base whitespace-nowrap mt-[2px]">
                  {ex.limit} Requests/{ex.unit.slice(0, 3).toLowerCase()}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ───────────── Exceptions (EDIT MODE) ───────────── */}
        {showExceptions && isEditing && endpoint.exceptions?.length > 0 && (
          <div className="mt-3">
            <ExceptionSection
              exceptions={endpoint.exceptions}
              userOptions={userOptions}
              onAddRow={() => onAddException?.(index)}
              onClearAll={() => onClearExceptions?.(index)}
              onUpdate={(i: number, patch: any) => onUpdateException?.(index, i, patch)}
              onRemove={(i: number) => onRemoveException?.(index, i)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------------
 * ApiCard
 * ------------------------------------------------------------------------- */

export const ApiCard: FC<any> = ({
  title,
  endpoints,
  showExceptions = false,
  isEditing,
  isSaving = false,
  userOptions,
  onStartEdit,
  onCancelEdit,
  onSave,
  ...handlers
}) => (
  <Spin spinning={isSaving}>
    <Card className="rounded-xl shadow-sm">
      <div className="flex justify-between mb-2">
        <Typography.Title level={3}>{title}</Typography.Title>

        {!isEditing ? (
          <Button
            className="font-semibold text-base"
            type="link"
            icon={<EditOutlined />}
            onClick={onStartEdit}
            disabled={isSaving}
          >
            Edit
          </Button>
        ) : (
          <Space>
            <Button className="font-semibold" onClick={onCancelEdit} disabled={isSaving}>
              Cancel
            </Button>
            <Button className="font-semibold" type="link" onClick={onSave} loading={isSaving}>
              Save
            </Button>
          </Space>
        )}
      </div>

      {endpoints.map((ep: Endpoint, i: number) => (
        <EndpointRow
          key={i}
          endpoint={ep}
          index={i}
          isEditing={isEditing}
          showExceptions={showExceptions}
          userOptions={userOptions}
          {...handlers}
        />
      ))}
    </Card>
  </Spin>
);
